import * as Sentry from "@sentry/react";

// Initialize Sentry as early as possible
Sentry.init({
  dsn: "https://1d1e6955046823489a3a48444cf1ef5f@o4509950685741056.ingest.us.sentry.io/4509950702059520",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    // Log console.error, console.warn to Sentry
    Sentry.consoleLoggingIntegration({ levels: ["error", "warn"] }),
    // User feedback integration
    Sentry.feedbackIntegration({
      colorScheme: "light",
      autoInject: false, // No floating button - we'll trigger manually
      showBranding: false,
      formTitle: "Report an Issue",
      submitButtonLabel: "Send Report",
      cancelButtonLabel: "Cancel",
      messageLabel: "Please describe what went wrong:",
      successMessage: "Thank you! We've received your report and will look into it.",
      errorMessage: "Sorry, there was an error sending your report. Please try again.",
      showEmail: true,
      showName: true,
      isEmailRequired: false,
      isNameRequired: false,
      emailLabel: "Email (optional)",
      nameLabel: "Name (optional)"
    })
  ],
  // Performance Monitoring
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
  // Set 'tracePropagationTargets' for test APIs
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/localhost/,
    "http://sws-api.staging",
    "http://hq-apps-testing.tailc36a4e.ts.net"
  ],
  // Session Replay
  replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0.5, // 10% in prod, 50% in dev
  replaysOnErrorSampleRate: 1.0, // 100% when errors occur
  // Enable logs
  enableLogs: true,
  // Send default PII (IP addresses, etc)
  sendDefaultPii: true,
  // Environment
  environment: process.env.NODE_ENV || 'development',
  // Release tracking (optional, can be set via build process)
  // release: process.env.REACT_APP_VERSION,
});

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/ios-fixes.css';
import App from './App';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50">
        <div className="text-center p-8 max-w-lg">
          <h1 className="text-2xl font-bold text-secondary-900 mb-4">
            Something went wrong
          </h1>
          <p className="text-secondary-600 mb-4">
            We're sorry for the inconvenience. The error has been reported and we'll look into it.
          </p>
          <details className="text-left mb-4 p-4 bg-white rounded-lg shadow">
            <summary className="cursor-pointer text-sm text-secondary-700 font-medium">
              Error details
            </summary>
            <pre className="mt-2 text-xs text-error-600 overflow-auto">
              {error?.toString()}
            </pre>
          </details>
          <button
            onClick={resetError}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )} showDialog>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// Unregister any service worker whose scope doesn't match the current app
// scope. Needed for two cases:
//   1. Legacy /sw.js registrations from older builds.
//   2. Prior installs that registered /service-worker.js at origin root; if we
//      now serve the PWA under /pwa/, those origin-root SWs intercept the
//      whole site and cache the wrong documents (e.g., cached the backend's
//      redirect-to-/app for the / URL), producing blink/reload loops.
if ('serviceWorker' in navigator) {
  // Reason: CRA's dev server exposes PUBLIC_URL as an empty string when
  // `homepage` is relative (e.g. "."). The previous expression mixed the
  // `|| '/'` fallback with a raw `.endsWith('/')` check on the empty string,
  // producing "//", which `new URL("//", origin)` rejects ("// is not a valid
  // URL") and crashed app startup. Normalize to a single base value first so an
  // empty/missing PUBLIC_URL cleanly resolves to the origin root.
  const publicBase = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '' ? process.env.PUBLIC_URL : '/';
  const expectedScope = new URL(
    publicBase.endsWith('/') ? publicBase : publicBase + '/',
    window.location.origin
  ).href;
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      const scriptUrl = registration.active?.scriptURL || '';
      if (scriptUrl.includes('/sw.js') || registration.scope !== expectedScope) {
        registration.unregister();
        console.log('Unregistered SW (scope mismatch):', registration.scope, 'expected', expectedScope);
      }
    }
  });
}

// Register service worker for PWA functionality (scoped under PUBLIC_URL).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // (2026-09-09, Jason Cox) updateViaCache: 'none' so the worker script itself
    // is always revalidated against the network. Without it a cached
    // service-worker.js can delay picking up a new deploy by up to 24h.
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`, {
      updateViaCache: 'none',
    })
      .then((registration) => {
        console.log('SW registered: ', registration);
      })
      .catch((registrationError) => {
        console.log('SW registration failed: ', registrationError);
      });
  });
}